import React, { useState, useEffect, useRef } from 'react';
import './Navbar.css';
import Logo from './Logo';
import ThemeToggle from './ThemeToggle';
import { smoothScrollTo } from '../utils/smoothScroll';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const navRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      const sections = ['hero', 'about', 'services', 'portfolio', 'evaluation', 'tools', 'feedback', 'contact'];
      const scrollPosition = window.scrollY + 120;

      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i]);
        if (el && el.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile drawer when clicking or tapping outside
  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (isMobileMenuOpen && navRef.current && !navRef.current.contains(e.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isMobileMenuOpen) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('touchstart', handleOutsideClick);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobileMenuOpen]);

  const scrollToSection = (sectionId) => {
    smoothScrollTo(sectionId, 85, 1100);
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { id: 'about', label: 'About' },
    { id: 'services', label: 'Solutions' },
    { id: 'portfolio', label: 'Work' },
    { id: 'evaluation', label: 'Audit' },
    { id: 'tools', label: 'Stack' },
    { id: 'feedback', label: 'Reviews' },
    { id: 'contact', label: 'Contact' },
  ];

  return (
    <>
      {/* Semi-transparent backdrop when mobile drawer is open */}
      <div
        className={`mobile-nav-backdrop ${isMobileMenuOpen ? 'backdrop-active' : ''}`}
        onClick={() => setIsMobileMenuOpen(false)}
        aria-hidden="true"
      />

      <header ref={navRef} className={`modern-navbar-wrapper ${isScrolled ? 'is-scrolled' : ''}`}>
      <div className="modern-navbar-capsule">
        {/* Brand */}
        <div className="modern-brand" onClick={() => scrollToSection('hero')}>
          <div className="brand-logo-frame">
            <Logo />
          </div>
          <div className="brand-name-group">
            <span className="brand-title">Trevox</span>
          </div>
        </div>

        {/* Desktop Nav Links */}
        <nav className="modern-nav-links">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className={`modern-nav-item ${activeSection === item.id ? 'active' : ''}`}
            >
              {item.label}
            </button>
          ))}
        </nav>

        {/* Right Actions Cluster */}
        <div className="modern-nav-actions">
          <ThemeToggle />

          {/* Mobile Hamburger */}
          <button
            className={`modern-hamburger ${isMobileMenuOpen ? 'open' : ''}`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle Navigation"
          >
            <span className="ham-line"></span>
            <span className="ham-line"></span>
          </button>
        </div>
      </div>

      {/* Mobile Drawer Dropdown */}
      <div className={`modern-mobile-drawer ${isMobileMenuOpen ? 'drawer-open' : ''}`}>
        <div className="mobile-drawer-content">
          <div className="mobile-drawer-links">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className={`mobile-drawer-link ${activeSection === item.id ? 'active' : ''}`}
              >
                <span>{item.label}</span>
                <span className="drawer-arrow">➔</span>
              </button>
            ))}
          </div>

          <div className="mobile-drawer-footer">
            <button
              className="btn btn-primary mobile-drawer-cta"
              onClick={() => scrollToSection('contact')}
            >
              Request Strategic Consultation ➔
            </button>
          </div>
        </div>
      </div>
    </header>
    </>
  );
};

export default Navbar;


